import { useEffect, useState } from 'react';
import type { Project, ProjectOrigin } from '@feedback-system/types';
import {
  fetchProjects,
  createProject,
  rotateProjectToken,
  fetchProjectOrigins,
  addProjectOrigin,
  deleteProjectOrigin,
} from '../api';

interface RevealedToken {
  projectId: string;
  projectName: string;
  token: string;
}

function TokenBanner({ revealed, onDismiss }: { revealed: RevealedToken; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable (e.g. non-HTTPS); user can still select the text manually.
    }
  };

  return (
    <div className="card bg-accent-green/10 border border-accent-green/30 space-y-2">
      <p className="text-accent-green text-sm font-medium">
        Token for "{revealed.projectName}" — copy it now, it will not be shown again:
      </p>
      <div className="flex gap-2 items-center">
        <code className="flex-1 bg-dark-900 rounded px-3 py-2 text-xs break-all text-dark-100">
          {revealed.token}
        </code>
        <button onClick={copy} className="btn-secondary text-xs whitespace-nowrap">
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <button onClick={onDismiss} className="text-xs text-dark-200 hover:underline">
        Dismiss
      </button>
    </div>
  );
}

function OriginsManager({ project }: { project: Project }) {
  const [origins, setOrigins] = useState<ProjectOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOrigin, setNewOrigin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setOrigins(await fetchProjectOrigins(project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load origins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const handleAdd = async () => {
    if (!newOrigin.trim()) return;
    try {
      setError(null);
      await addProjectOrigin(project.id, newOrigin.trim());
      setNewOrigin('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add origin');
    }
  };

  const handleDelete = async (originId: string) => {
    try {
      setError(null);
      await deleteProjectOrigin(project.id, originId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove origin');
    }
  };

  return (
    <div className="space-y-3 pt-3 border-t border-dark-700">
      <p className="text-xs font-medium text-dark-200">Allowed origins</p>
      {error && <p className="text-accent-red text-xs">{error}</p>}
      {loading ? (
        <p className="text-dark-200 text-xs">Loading…</p>
      ) : origins.length === 0 ? (
        <p className="text-dark-200 text-xs">No origins allowed yet — widget submissions will be rejected in production.</p>
      ) : (
        <ul className="space-y-1">
          {origins.map((o) => (
            <li key={o.id} className="flex items-center justify-between text-sm bg-dark-900 rounded px-3 py-1.5">
              <span className="text-dark-100 font-mono text-xs break-all">{o.origin}</span>
              <button onClick={() => handleDelete(o.id)} className="text-accent-red text-xs hover:underline ml-3 shrink-0">
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="https://example.com"
          value={newOrigin}
          onChange={(e) => setNewOrigin(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="input text-sm"
        />
        <button onClick={handleAdd} className="btn-secondary text-xs whitespace-nowrap">
          Add origin
        </button>
      </div>
    </div>
  );
}

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealedToken | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setProjects(await fetchProjects());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!newProjectName.trim() || creating) return;
    try {
      setCreating(true);
      setError(null);
      const project = await createProject(newProjectName.trim());
      setNewProjectName('');
      setRevealed({ projectId: project.id, projectName: project.name, token: project.token });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleRotate = async (project: Project) => {
    try {
      setError(null);
      const updated = await rotateProjectToken(project.id);
      setRevealed({ projectId: project.id, projectName: project.name, token: updated.token });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate token');
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-dark-100">Projects</h1>

      {revealed && <TokenBanner revealed={revealed} onDismiss={() => setRevealed(null)} />}

      {error && (
        <div className="card bg-accent-red/10 border border-accent-red/30">
          <p className="text-accent-red text-sm">{error}</p>
        </div>
      )}

      <div className="card space-y-3">
        <p className="text-sm font-medium text-dark-100">New project</p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="input"
          />
          <button onClick={handleCreate} disabled={creating} className="btn-primary whitespace-nowrap">
            {creating ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-12">
          <p className="text-dark-200">Loading projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-dark-200">No projects yet — create one above to get a widget token.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => (
            <div key={project.id} className="card space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-100 font-medium">{project.name}</p>
                  <p className="text-dark-200 text-xs font-mono">{project.id}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handleRotate(project)} className="btn-secondary text-xs">
                    Rotate token
                  </button>
                  <button
                    onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}
                    className="btn-secondary text-xs"
                  >
                    {expandedId === project.id ? 'Hide origins' : 'Manage origins'}
                  </button>
                </div>
              </div>
              {expandedId === project.id && <OriginsManager project={project} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
